/* eslint-disable max-lines */
import { useCameraGroups, useCameras } from '@hakimo-ui/hakimo/data-access';
import { ListView } from '@hakimo-ui/hakimo/feature-shared';
import { CamFilterValues, Camera } from '@hakimo-ui/hakimo/types';
import { CameraGroupIcon, MenuDropdown } from '@hakimo-ui/hakimo/ui-elements';
import { FilterPanel } from '@hakimo-ui/hakimo/ui-filters';
import { PageLayout } from '@hakimo-ui/hakimo/ui-layout';
import {
  trackFilters,
  useCanAddCamera,
  useCanAddCameraGroup,
  useCanViewCameraGroup,
  withAuthz,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import { Alert } from '@hakimo-ui/shared/ui-base';
import {
  CameraIcon,
  PlusIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import CameraDetails from '../camera-details/CameraDetails';
import AddCameraGroup from '../camera-groups/AddCameraGroup';
import CameraGroupDetails from '../camera-groups/CameraGroupDetails';
import CameraGroupItem from '../camera-groups/CameraGroupItem';
import { getCameraGroupSearchParams } from '../camera-groups/util';
import { filterConfigs } from '../constants';
import { camFilterAtom, pageAtom, pageSizeAtom } from '../state';
import { AddCamera } from './add-camera/AddCamera';
import CameraItem from './CameraItem';
import CameraListHeader, { CameraListView } from './CameraListHeader';
import {
  getNextCameraIdInList,
  getSearchParams,
  isDefaultFilter,
} from './util';

interface Props {
  tenantIds?: string[];
}

function CamerasList(props: Props) {
  const navigate = useNavigate();
  const { tenantIds } = props;
  const [page, setPage] = useAtom(pageAtom);
  const [pageSize] = useAtom(pageSizeAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamCamId = searchParams.get('camId');
  const [searchedCameraName, setSearchedCameraName] = useState('');
  const canAddCamera = useCanAddCamera();
  const canViewCameraGroup = useCanViewCameraGroup();
  const canAddCameraGroup = useCanAddCameraGroup();
  const location = useLocation();
  const view: CameraListView =
    location.pathname.includes('/camera-group') && canViewCameraGroup
      ? 'groups'
      : 'list';
  const selectedGroupId = searchParams.get('cameraGroupId') ?? undefined;

  const [selectedCameraId, setSelectedCameraId] = useState<string | undefined>(
    searchParamCamId || undefined
  );
  const [nextCamIdInList, setNextCamIdInList] = useState<string>();
  const [cameraTotal, setCameraTotal] = useState(0);
  const [groupTotal, setGroupTotal] = useState(0);
  const [camFilters, setCamFilters] = useAtom(camFilterAtom);
  const [openFilterPanel, setOpenFilterPanel] = useState(false);
  const [addCamera, setAddCamera] = useState(false);
  const [addCameraGroup, setAddCameraGroup] = useState(false);

  const cameraSearchParams = useMemo(
    () =>
      getSearchParams(camFilters, {
        tenantIds,
        searchedCameraName,
        page,
        pageSize,
      }),
    [camFilters, tenantIds, searchedCameraName, page, pageSize]
  );

  const groupSearchParams = useMemo(
    () =>
      getCameraGroupSearchParams(
        page,
        pageSize,
        searchedCameraName,
        camFilters.location.values.map((loc) => loc.id)
      ),
    [page, pageSize, searchedCameraName, camFilters.location.values]
  );

  const { isFetching, isError, error, data } = useCameras(
    cameraSearchParams,
    view === 'list'
  );

  const { data: groupData, isFetching: isFetchingGroups } = useCameraGroups(
    groupSearchParams,
    view === 'groups' && canViewCameraGroup
  );

  useEffect(() => {
    if (view === 'list' && data) {
      setCameraTotal(data.total);
    }
  }, [data, view]);

  useEffect(() => {
    if (view === 'groups' && groupData) {
      setGroupTotal(groupData.total);
    }
  }, [groupData, view]);

  useEffect(() => {
    view === 'groups' && setSelectedCameraId(undefined);
  }, [view]);

  const onViewChange = (nextView: CameraListView) => {
    if (nextView === view) return;
    if (nextView === 'groups' && !canViewCameraGroup) return;
    setPage(1);
    setSearchParams({});
    navigate(nextView === 'groups' ? '/cameras/camera-group' : '/cameras');
  };

  const changeSelected = useCallback(
    (camera: Camera) => {
      setSelectedCameraId(camera.id);
      setNextCamIdInList(getNextCameraIdInList(camera.id, data?.items));
      setSearchParams([['camId', camera.id]]);
    },
    [data?.items, setSearchParams]
  );

  const onApplyFilters = (values: CamFilterValues) => {
    setCamFilters(values);
    trackFilters(values);
    setPage(1);
    setOpenFilterPanel(false);
  };

  const onSearchCameraName = (value: string) => {
    if (value !== searchedCameraName) {
      setSearchedCameraName(value);
      setPage(1);
    } else {
      setSearchedCameraName(value);
    }
  };

  const onDeletedCamera = () => {
    setSelectedCameraId(nextCamIdInList);
    setNextCamIdInList(getNextCameraIdInList(nextCamIdInList, data?.items));
  };

  const selectedCameraType = data?.items.find(
    (item) => item.id === selectedCameraId
  )?.integrationType;

  const isDataValid =
    view === 'list'
      ? !!data && data.items.length > 0
      : !!groupData && groupData.items.length > 0;

  const secondaryAction = (
    <MenuDropdown
      items={[
        {
          name: 'Add Camera',
          icon: <VideoCameraIcon className="h-5 w-5" />,
          onClick: () => setAddCamera(true),
        },
        {
          name: 'Add Camera Group',
          icon: <CameraGroupIcon className="h-5 w-5" />,
          onClick: () => setAddCameraGroup(true),
        },
      ]}
      menuButton={<PlusIcon className="h-5 w-5" />}
    />
  );

  return (
    <>
      <PageLayout
        variant="split"
        title="Cameras"
        icon={VideoCameraIcon}
        secondaryAction={
          canAddCamera || canAddCameraGroup ? secondaryAction : undefined
        }
      >
        <PageLayout.List>
          <>
            {isError && (
              <div className="mb-2">
                <Alert type="error">{error.message}</Alert>
              </div>
            )}

            <CameraListHeader
              onClickOpenFilterPanel={() => setOpenFilterPanel(true)}
              isDefault={isDefaultFilter(camFilters)}
              isLoading={view === 'list' ? isFetching : isFetchingGroups}
              onSearch={onSearchCameraName}
              camFilters={camFilters}
              isDataValid={isDataValid}
              view={view}
              onViewChange={onViewChange}
              canViewCameraGroup={canViewCameraGroup}
            />
            <div className="p-2">
              {view === 'list' && (
                <ListView
                  items={data?.items || []}
                  total={cameraTotal}
                  page={page}
                  pageSize={pageSize}
                  onChangePage={setPage}
                  renderListItem={(camera) => (
                    <CameraItem
                      key={camera.id}
                      camera={camera}
                      selected={camera.id === selectedCameraId}
                      onClick={() => changeSelected(camera)}
                    />
                  )}
                />
              )}
              {view === 'groups' && canViewCameraGroup && (
                <ListView
                  items={(groupData?.items || []).map((group) => ({
                    ...group,
                    id: group.cameraGroupId,
                  }))}
                  total={groupTotal}
                  page={page}
                  pageSize={pageSize}
                  onChangePage={setPage}
                  renderListItem={(group) => (
                    <CameraGroupItem
                      group={group}
                      selected={group.cameraGroupId === selectedGroupId}
                      onClick={() =>
                        setSearchParams((prev) => {
                          const params = new URLSearchParams(prev);
                          params.set('cameraGroupId', group.cameraGroupId);
                          return params;
                        })
                      }
                    />
                  )}
                />
              )}
            </div>
          </>
        </PageLayout.List>
        <PageLayout.Details>
          {addCameraGroup && (
            <AddCameraGroup onClose={() => setAddCameraGroup(false)} />
          )}
          {!addCameraGroup &&
            view === 'list' &&
            (selectedCameraId ? (
              <CameraDetails
                key={selectedCameraId}
                camId={selectedCameraId}
                camType={selectedCameraType}
                onDeletedCamera={onDeletedCamera}
              />
            ) : (
              <div className="dark:border-ondark-line-2 dark:text-ondark-text-2 border-ondark-text-2 text-onlight-text-3 ml-4 flex h-full items-center justify-center border-2 border-dashed">
                <CameraIcon className="mr-2 h-8 w-8" />
                <div>Select a camera to check its details</div>
              </div>
            ))}
          {!addCameraGroup &&
            view === 'groups' &&
            (selectedGroupId ? (
              <CameraGroupDetails
                cameraGroupId={selectedGroupId}
                onDeleted={() =>
                  setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    params.delete('cameraGroupId');
                    return params;
                  })
                }
              />
            ) : (
              <div className="dark:border-ondark-line-2 dark:text-ondark-text-2 border-ondark-text-2 text-onlight-text-3 ml-4 flex h-full items-center justify-center border-2 border-dashed">
                <CameraGroupIcon className="mr-2 h-6 w-6" />
                <div>Select a camera group to see details</div>
              </div>
            ))}
        </PageLayout.Details>
      </PageLayout>
      <FilterPanel
        open={openFilterPanel}
        title="Camera Filters"
        filterConfigs={filterConfigs}
        initialFilterValues={camFilters}
        trackingEventNameApply="apply_cam_filters"
        trackingEventNameReset="reset_cam_filters"
        onClose={() => setOpenFilterPanel(false)}
        onApply={onApplyFilters}
      />
      {addCamera && <AddCamera onClose={() => setAddCamera(false)} />}
    </>
  );
}
export default withAuthz(withErrorBoundary(CamerasList), ['camera:view']);
